import { Info, ArrowRight } from 'lucide-react';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/helpers/utils';

const STORAGE_KEY = 'crowdfpl_duels_onboarding_dismissed';

interface DuelsOnboardingProps {
  className?: string;
}

export function DuelsOnboarding({ className }: DuelsOnboardingProps) {
  const [isDismissed, setIsDismissed] = useState(true); // Default to true to prevent flash
  const [isCollapsing, setIsCollapsing] = useState(false);

  useEffect(() => {
    const dismissed = localStorage.getItem(STORAGE_KEY);
    setIsDismissed(dismissed === 'true');
  }, []);

  const handleDismiss = () => {
    setIsCollapsing(true);
    // Wait for animation to complete before hiding
    setTimeout(() => {
      localStorage.setItem(STORAGE_KEY, 'true');
      setIsDismissed(true);
    }, 400);
  };

  if (isDismissed) {
    return null;
  }

  return (
    <div
      className={cn(
        'rounded-xl border border-emerald-200 bg-emerald-50 dark:border-emerald-800/50 dark:bg-emerald-950/30',
        'overflow-hidden',
        className,
      )}
      style={{
        transition: 'max-height 400ms cubic-bezier(0.4, 0, 0.2, 1), opacity 300ms ease-out, margin 400ms ease-out',
        maxHeight: isCollapsing ? '0px' : '250px',
        opacity: isCollapsing ? 0 : 1,
        marginTop: isCollapsing ? '-8px' : undefined,
      }}
    >
      <div className="p-4 sm:p-5">
        <div className="flex gap-3">
          {/* Icon */}
          <div className="shrink-0">
            <div className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-emerald-600 dark:border-emerald-400">
              <Info className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 space-y-3">
            {/* Title */}
            <h3 className="font-semibold text-foreground">
              How duels and ratings work
            </h3>

            {/* Links */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <Link
                to="/how-it-works"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700 hover:text-emerald-800 dark:text-emerald-400 dark:hover:text-emerald-300 transition-colors"
              >
                How it works
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
              <span className="text-muted-foreground hidden sm:inline">·</span>
              <Link
                to="/rating"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700 hover:text-emerald-800 dark:text-emerald-400 dark:hover:text-emerald-300 transition-colors"
              >
                Rating system
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>

            {/* Footer note */}
            <p className="text-sm text-muted-foreground">
              You can always find these links in the footer.
            </p>
          </div>
        </div>

        {/* Dismiss button */}
        <div className="flex justify-end mt-2 -mb-1">
          <button
            onClick={handleDismiss}
            className="text-sm font-medium text-emerald-700 hover:text-emerald-800 dark:text-emerald-400 dark:hover:text-emerald-300 transition-colors"
          >
            Don't show again
          </button>
        </div>
      </div>
    </div>
  );
}

